import { ReactNode, useContext, useEffect } from 'react';

import { MDText } from 'i18n-react';

import { Hyperlink } from '@motorway/mw-highway-code/alpha';

import cypressIds from 'CypressId';

import { applyCypressData } from 'Utilities';
import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';
import { VERIFICATION_ANALYTICS_EVENTS } from 'Utilities/analytics/analyticsDocumentVerification';

import { StepList } from 'Storybook/components/local/StepList/StepList';

import PageLoader from '../../../../components/transitions/PageLoader/PageLoader';
import { FAMILY_KEY } from '../../vehicleProfiling/VehicleProfiling.constants';
import LocalTexts from '../DocumentCapture.texts.json';
import { DocumentCaptureOfferContext } from '../DocumentCaptureContexts';
import { useIncodeVerificationDocuments } from '../uploadDocuments/pages/IncodeSDKPage/hooks/useIncodeVerificationDocuments';

import docReviewImageSrc from './assets/docReview.svg';
import { KeeperVerificationBadge } from './KeeperVerificationBadge';
import { useBankDetailsRedirect } from './useBankDetailsRedirect';

import styles from './ReviewStatusSubview.scss';

const LocalT = new MDText(LocalTexts.reviewStatus);

// eslint-disable-next-line operator-linebreak
const collectionDayArticleLink =
  'https://help.motorway.co.uk/hc/en-gb/articles/4410620430866-What-happens-on-the-day-of-collection';

const onHelpArticleClick = () => {
  DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.WHAT_TO_EXPECT_LINK_CLICKED();
};

type GetStepsProps = { acceptedBidDealerName?: string };

const getSteps = ({ acceptedBidDealerName }: GetStepsProps) => [
  {
    content: (
      <div className={styles.docsReviewStep}>
        <h3 className={styles.title}>{LocalT.translate('inProgress.reviewDocumentsStep.title')}</h3>
        <p className={styles.context}>
          {LocalT.translate('inProgress.reviewDocumentsStep.context', {
            dealerName: acceptedBidDealerName ? `, ${acceptedBidDealerName}` : '',
          })}
        </p>
      </div>
    ),
    id: 1,
  },
  {
    content: (
      <div className={styles.docsReviewStep}>
        <h3 className={styles.title}>{LocalT.translate('inProgress.scheduleCollectionStep.title')}</h3>
        <p className={styles.context}>{LocalT.translate('inProgress.scheduleCollectionStep.context')}</p>
        <div className={styles.link}>
          <Hyperlink
            {...applyCypressData(cypressIds.links.collectionDayArticleLinkLabel)}
            href={collectionDayArticleLink}
            label={LocalT.translate('inProgress.scheduleCollectionStep.hyperlinkLabel')}
            onClick={onHelpArticleClick}
            size="small"
            target='_blank'
          />
        </div>
      </div>
    ),
    id: 2,
  },
];

export const ReviewStatusSubview = ({ statusText = null }: { statusText?: ReactNode }) => {
  const { bankDetailsIncomplete, isDataLoaded } = useBankDetailsRedirect();
  const { acceptedBid, data, id } = useContext(DocumentCaptureOfferContext);
  const { data: verificationData, isLoading, keeperData } = useIncodeVerificationDocuments({ enquiryId: id });

  const { saleVerificationId } = verificationData || {};
  const isFamilyFriendKeeper = data?.seller?.keeperType === FAMILY_KEY;
  const keeperName = data?.seller?.keeperName;
  const keeperVerificationStatus = keeperData?.verificationStatus;

  const steps = getSteps({ acceptedBidDealerName: acceptedBid?.dealer?.name });

  useEffect(() => {
    VERIFICATION_ANALYTICS_EVENTS.DOCS_IN_REVIEW({});
  }, []);

  if (isLoading || !isDataLoaded || bankDetailsIncomplete) {
    return <PageLoader />;
  }

  const displayBadge = !!(isFamilyFriendKeeper && keeperVerificationStatus && saleVerificationId);

  return (
    <div className={styles.reviewStatusSubview} {...applyCypressData(cypressIds.sections.reviewStatusSubview)}>
      <div className={styles.heading}>
        <div className={styles.imageContainer}>
          <img alt="docReviewImage" className={styles.docReviewImage} src={docReviewImageSrc} />
        </div>
        <h1 className={styles.headingTitle}>{LocalT.translate('inProgress.newTitle', {
          ending: isFamilyFriendKeeper ? LocalT.translate('inProgress.titleEnding') : '',
        })}</h1>
        <p className={styles.headingSubTitle}>{LocalT.translate('inProgress.newSubtitle')}</p>
      </div>
      {displayBadge && <KeeperVerificationBadge {...{ keeperName, keeperVerificationStatus, saleVerificationId }} />}
      <div>
        <h2 className={styles.whatHappensNext}>{LocalT.translate('inProgress.whatHappensNext')}</h2>
        <StepList steps={steps} />
        {statusText && <div className={styles.statusText}>{statusText}</div>}
      </div>
    </div>
  );
};
